import { useAppContext } from 'context/AppContext/AppContext';
import { useRouter } from 'next/router';

export enum FORM_TYPE {
  AFFILIATE = 'affiliate',
  INDIVIDUAL = 'individual',
  AFFILIATE_ADS = 'affiliate-ads',
  INDIVIDUAL_ADS = 'individual-ads',
  AFFILIATE_A1 = 'affiliate-a1',
  INDIVIDUAL_A1 = 'individual-a1',
  // -adsd must match the KYC service's FormURLSuffix() for the ADSD entity
  AFFILIATE_ADSD = 'affiliate-adsd',
  INDIVIDUAL_ADSD = 'individual-adsd',
  RFI = 'rfi',
}

/** Next.js exposes repeated query params as arrays; take the first value. */
function firstQueryValue(
  value: string | string[] | undefined,
): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

/**
 * Hook that returns form type information and derived properties from URL parameters.
 *
 * @returns An object containing:
 *   - formType: The form type value from URL parameters
 *   - isAffiliateApplication: Whether this is an affiliate application
 *   - isIndividualApplication: Whether this is an individual application
 *   - isAdsApplication: Whether this is an ads-related application
 *   - isA1Application: Whether this is an A1-related application
 *   - isAdsdApplication: Whether this is a Swap Dealer (ADSD) application
 *   - isRfiForm: Whether this is an RFI form
 *   - isAuthorizedUser: Whether the user has the authorized user role
 *   - isArmadaApplication: Whether the form URL carries `product=agentic-banking`. The kyc service sets it for Armada
 *     institution affiliates (see institutional.AffiliateTypeArmadaInstitution in the Go monorepo) and the app
 *     switches to Armada-specific onboarding copy.
 */
export const useFormType = (): {
  formType: FORM_TYPE | undefined;
  isAffiliateApplication: boolean;
  isIndividualApplication: boolean;
  isAdsApplication: boolean;
  isA1Application: boolean;
  isAdsdApplication: boolean;
  isRfiForm: boolean;
  isAuthorizedUser?: boolean;
  isArmadaApplication: boolean;
} => {
  const router = useRouter();
  const formType = firstQueryValue(router.query.formType) as
    | FORM_TYPE
    | undefined;
  const { role } = useAppContext().sessionInfo;
  const product = firstQueryValue(router.query.product);

  return {
    formType,
    isAffiliateApplication:
      formType === FORM_TYPE.AFFILIATE ||
      formType === FORM_TYPE.AFFILIATE_ADS ||
      formType === FORM_TYPE.AFFILIATE_A1 ||
      formType === FORM_TYPE.AFFILIATE_ADSD,
    isIndividualApplication:
      formType === FORM_TYPE.INDIVIDUAL ||
      formType === FORM_TYPE.INDIVIDUAL_ADS ||
      formType === FORM_TYPE.INDIVIDUAL_A1 ||
      formType === FORM_TYPE.INDIVIDUAL_ADSD,
    isAdsApplication: formType?.endsWith('-ads') || false,
    isA1Application: formType?.endsWith('-a1') || false,
    isAdsdApplication: formType?.endsWith('-adsd') || false,
    isRfiForm: formType === FORM_TYPE.RFI,
    isAuthorizedUser: formType === FORM_TYPE.INDIVIDUAL_ADS && role === 'au',
    isArmadaApplication: product === 'agentic-banking',
  };
};
